import { useCallback, useEffect } from "react";

import {InitialOrderDataPart, OrderFields} from "../../constants";
import useOrderData from "../../context/useOrderData";
import useOrderStorage from "../storage/useOrderStorage";
import useOrderFormik from "../useOrderFormik";
import filterPrefillParams from "./prefill/filterPrefillParams";
import getPrefillCommonData from "./prefill/getPrefillCommonData";
import getPrefillNewAddressData from "./prefill/getPrefillNewAddressData";
import getPrefillPackageData from "./prefill/getPrefillPackageData";
import getPrefillReceiverData from "./prefill/getPrefillReceiverData";
import getPrefillSenderData from "./prefill/getPrefillSenderData";
import getPrefillSummaryData from "./prefill/getPrefillSummaryData";

const usePrefillData = (): void => {
  let { setValues } = useOrderFormik();

  const { setInitialDataPartLoaded } = useOrderData();

  const {
    isEdit,
    staticData: { affiliateData },
    defaultUserAddress,
    invoiceAddresses,
    prefillData,
    dynamicData: { packageSizes },
    getShipmentEditData,
  } = useOrderData();

  const { getData } = useOrderStorage();

  const loadEditShipment = useCallback(async () => {
    const editData = await getShipmentEditData();
    if (editData) {
      setValues((prevValues) => ({
        ...editData,
        [OrderFields.WEIGHT_REGULATIONS]: prevValues[OrderFields.WEIGHT_REGULATIONS],
      }));
    }
    setInitialDataPartLoaded(InitialOrderDataPart.PREFILL_VALUES);
  }, [getShipmentEditData, setInitialDataPartLoaded, setValues]);

  useEffect(() => {
    if (isEdit) {
      loadEditShipment();
    }
  }, [isEdit, loadEditShipment]);

  useEffect(() => {
    // No data prefilling if it is edit of existing shipment
    if (isEdit) {
      return;
    }

    const storedData = getData();
    const prefillParams = filterPrefillParams({
      prefill: prefillData,
      affiliate: affiliateData || undefined,
      userAddress: defaultUserAddress,
      invoiceAddresses,
      storedData,
      packageSizes,
    });
    const commonData = getPrefillCommonData(prefillParams);
    const packageInfoData = getPrefillPackageData(prefillParams);
    const senderData = getPrefillSenderData(prefillParams);
    const receiverData = getPrefillReceiverData(prefillParams);
    const summaryData = getPrefillSummaryData(prefillParams);
    const newAddressData = getPrefillNewAddressData(prefillParams);

    const dataToPrefill = {
      ...commonData,
      ...packageInfoData,
      ...senderData,
      ...receiverData,
      ...summaryData,
      ...newAddressData,
    };
    Object.keys(dataToPrefill).forEach((fieldName) => {
      if (typeof dataToPrefill[fieldName] === "undefined" || dataToPrefill[fieldName] === null) {
        delete dataToPrefill[fieldName];
      }
    });

    setValues((oldValues) => ({ ...oldValues, ...dataToPrefill }));

    setInitialDataPartLoaded(InitialOrderDataPart.PREFILL_VALUES);
  }, [
    getData,
    defaultUserAddress,
    invoiceAddresses,
    prefillData,
    setValues,
    affiliateData,
    isEdit,
    setInitialDataPartLoaded,
  ]);
};
export default usePrefillData;
